import { useState, useEffect, useCallback } from "react";
import { useContextSelector } from "use-context-selector";
import AuthContext from "../../context/AuthContext";
import BetSlipContext from "../../context/BetSlipContext";
import { isEmpty } from "lodash";
import useScrollToItem from "../../component/Scroll";
import { useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";

export const useFancyLogic = ({
  fancy,
  detail,
  favouriteList = [],
  fancyHeaderKey = "All",
  fancyTab,
}) => {
  const location = useLocation();
  let loc = location?.pathname?.split("/");
  const [data, setData] = useState([]);
  const [localLimit, setLocalLimit] = useState({});

  // Auth & BetSlip contexts
  const user = useContextSelector(AuthContext, (ctx) => ctx.user);
  const oneclickActive = useContextSelector(
    AuthContext,
    (ctx) => ctx.oneclickActive,
  );
  const oneClickBid = useContextSelector(AuthContext, (ctx) => ctx.oneClickBid);
  const profileData = useContextSelector(AuthContext, (ctx) => ctx.profileData);

  const setShowBetDialog = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setShowBetDialog,
  );
  const setBetSlipObject = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setBetSlipObject,
  );
  const betSlipObject = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.betSlipObject,
  );
  const showBetDialog = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.showBetDialog,
  );
  const setLimit = useContextSelector(BetSlipContext, (ctx) => ctx.setLimit);
  const setOneClickBetLoader = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setOneClickBetLoader,
  );
  const handelPlaceFancy = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.handelPlaceFancy,
  );
  const setToastObject = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setToastObject,
  );

  const scrollToItem = useScrollToItem("#header");

  // Reorder favorites
  const reorderArray = useCallback((ids, data) => {
    const favoriteIdsSet = new Set(ids.map(String));
    const favorites = data.filter((item) =>
      favoriteIdsSet.has(String(item.selectionId)),
    );
    const nonFavorites = data.filter(
      (item) => !favoriteIdsSet.has(String(item.selectionId)),
    );
    return [...favorites, ...nonFavorites];
  }, []);

  // Set local limits
  useEffect(() => {
    if (!isEmpty(detail) || !isEmpty(profileData)) {
      const obj =
        detail?.matchSetting?.length > 0
          ? detail?.matchSetting?.find((res) => res?.type === "fancy")
          : profileData?.matchSetting?.find(
              (res) =>
                res?.type === "fancy" && res?.sportType == detail?.gameType,
            );

      const findNewLimit = detail?.limit?.find((res) => res?.type === "fancy");
      if (!isEmpty(findNewLimit)) {
        setLocalLimit({
          ...obj,
          maxAmount: Math.min(
            Number(obj?.maxAmount),
            Number(findNewLimit?.maxAmount),
          ),
          oddsLimit: Math.min(
            Number(obj?.oddsLimit),
            Number(findNewLimit?.oddsLimit),
          ),
        });
      } else if (!isEmpty(obj)) {
        setLocalLimit(obj);
      } else {
        setLocalLimit({
          maxAmount: 1000,
          maxProfit: 10000,
          minAmount: 1,
          oddsLimit: 10,
        });
      }
    }
  }, [detail, profileData]);

  // Handle betting
  const handelChange = useCallback(
    (odds, type, team, item, e) => {
      if (isEmpty(user)) {
        setToastObject({
          type: "warning",
          title: "Warning",
          message: "Please login to place your bet!",
          status: true,
        });
        return;
      }

      if (odds?.rt <= 0) return;

      setShowBetDialog(true);
      if (loc[1] != "match-detail-sky" && !isMobile) {
        scrollToItem(e.currentTarget);
      }

      const object = {
        odds: odds?.rt,
        teamName: team,
        betType: type,
        playType: "fancy",
        object: {
          ...item,
          ...odds,
          teamName: team,
          eventId: detail?.eventId,
          marketId: detail?.marketId,
        },
      };

      setLimit(localLimit);
      setBetSlipObject((prev) => ({ ...prev, ...object }));

      // if (oneclickActive) {
      //   setOneClickBetLoader("fancy");
      //   handelPlaceFancy(localLimit, { ...object, bidPrice: oneClickBid });
      // }
    },
    [
      user,
      oneclickActive,
      localLimit,
      setBetSlipObject,
      setLimit,
      scrollToItem,
      handelPlaceFancy,
      setOneClickBetLoader,
      oneClickBid,
      setShowBetDialog,
      detail,
      setToastObject,
    ],
  );

  // Keep betSlipObject in sync with available fancy items
  useEffect(() => {
    if (betSlipObject?.playType !== "fancy") return;
    if (!showBetDialog) return;

    const find = fancy?.find(
      (res) => res?.centralizedId === betSlipObject?.object?.centralizedId,
    );

    if (isEmpty(find)) return;
    const rtArr = find?.odds?.rt ?? find?.rt ?? [];
    if ([9, 2, 3, 4].includes(Number(find?.ms))) {
      setShowBetDialog(false);
      setBetSlipObject({});
    } else {
      setBetSlipObject((prev) => {
        const betSideIndex = prev?.betType === "Yes" ? 1 : 0; // Yes = back, No = lay
        const nextOddsObj = rtArr?.[betSideIndex];

        // Don't overwrite with an object; odds must stay numeric (rt).
        const parsed = Number(nextOddsObj?.rt);
        const nextOddsValue = Number.isFinite(parsed) ? parsed : prev?.odds;

        if (!nextOddsObj) return prev;
        return {
          ...prev,
          odds: nextOddsValue,
          object: {
            ...prev.object,
            ...(nextOddsObj ?? {}),
          },
        };
      });
    }
  }, [
    fancy,
    betSlipObject?.object?.centralizedId,
    betSlipObject?.playType,
    betSlipObject?.betType,
    showBetDialog,
    setBetSlipObject,
  ]); // Keep in sync with live fancy odds

  // Filter data based on header
  useEffect(() => {
    if (!fancy?.length) return;

    const updatedArray =
      favouriteList?.length > 0 ? reorderArray(favouriteList, fancy) : fancy;
    const headerFilters = {
      WP: ["wkt", "runs", "face by"],
      OddEven: ["odd", "even"],
      Ball: ["only", "only run"],
      other: ["odd", "even", "wkt", "runs", "face by"],
    };

    let newData = [];
    if (fancyTab == "fancyPlus") {
      const keywords = ["lambi", "@ 1"];
      newData = updatedArray?.filter(({ name = "" }) => {
        const lowerName = name.toLowerCase();
        return keywords.some((key) => lowerName.includes(key));
      });
    } else {
      if (fancyHeaderKey === "All") {
        newData = updatedArray;
      } else if (fancyHeaderKey === "extra") {
        newData = updatedArray.filter((item) =>
          item?.name?.toLowerCase().includes("adv"),
        );
      } else if (headerFilters[fancyHeaderKey]) {
        const blacklist = headerFilters[fancyHeaderKey];
        newData = updatedArray.filter((item) => {
          const name = item?.name?.toLowerCase();
          return fancyHeaderKey === "other"
            ? !blacklist.some((word) => name.includes(word))
            : blacklist.some((word) => name.includes(word));
        });
      } else {
        newData = updatedArray.filter(
          (res) => res?.categoryType === fancyHeaderKey,
        );
      }
    }

    setData(newData);
  }, [fancy, fancyHeaderKey, favouriteList, fancyTab]);

  // Merge and calculate positions
  const mergeAndCalculate = useCallback((betSlipObject, apiData) => {
    if (!betSlipObject || !apiData?.betRunPosition) return 0;

    const { betType, bidPrice, odds: betRun, object } = betSlipObject;
    const pt = object?.pt || 0;

    const profitAmount =
      betType === "Yes" ? +((bidPrice * pt) / 100).toFixed(2) : +bidPrice;
    const loseAmount =
      betType === "Yes" ? -bidPrice : -+((bidPrice * pt) / 100).toFixed(2);

    let merged = apiData.betRunPosition.map((p) => ({
      betRun: p.betRun,
      position: p.position ?? 0,
    }));
    const minRun = Math.min(...merged.map((p) => p.betRun));
    const maxRun = Math.max(...merged.map((p) => p.betRun));

    if (betRun > maxRun)
      for (let i = maxRun + 1; i <= betRun; i++)
        merged.push({ betRun: i, position: 0 });
    else if (betRun < minRun)
      for (let i = betRun + 1; i < minRun; i++)
        merged.push({ betRun: i, position: 0 });

    merged.forEach((item) => {
      item.position +=
        betType === "Yes"
          ? item.betRun >= betRun
            ? profitAmount
            : loseAmount
          : item.betRun < betRun
            ? profitAmount
            : loseAmount;
    });

    merged.sort((a, b) => a.betRun - b.betRun);
    return Math.min(...merged.map((p) => p.position));
  }, []);

  return {
    data,
    localLimit,
    handelChange,
    mergeAndCalculate,
  };
};
